import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Skeleton, SkeletonColorSchemesEnum} from '../../../../../../../../src/components/common/skeleton';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {fontSizes, fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

interface CrmSyncsSectionWrapperProps {
  title?: ReactNode;
  description?: ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean;
  button?: ReactNode;
  paddingTop?: number;
  renderLoadingState?: Renderer;
}

/*
 * Style.
 */

interface StyledSectionWrapperProps {
  $paddingTop: number;
}

const StyledSectionWrapperDiv = styled.div<StyledSectionWrapperProps>`
  padding-bottom: 20px;
  padding-top: ${(p) => p.$paddingTop}px;
  width: 600px;
`;

interface StyledSectionContentWrapperDivProps {
  $isDisabled?: boolean;
}

const StyledSectionContentWrapperDiv = styled.div<StyledSectionContentWrapperDivProps>`
  ${(p) =>
    p.$isDisabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
`;

export const StyledSectionTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  line-height: ${fontSizes.large};
`;

const StyledDescriptionWrapperDiv = styled.div`
  margin-top: 8px;
  color: ${(p) => p.theme.greys.shade70};
  line-height: 22px;
  font-size: ${fontSizes.medium};
`;

export const StyledSectionHeaderWrapperDiv = styled.div`
  margin-bottom: 12px;
`;

export const StyledTitleWrapperDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;
`;

/*
 * Component.
 */

export const CrmSyncsSectionWrapper: FC<PropsWithChildren<CrmSyncsSectionWrapperProps>> = (props) => {
  const {
    title,
    description,
    isDisabled,
    isLoading,
    children,
    paddingTop = 20,
    renderLoadingState: renderLoading,
    button,
  } = props;
  if (isLoading) {
    return renderLoadingState(paddingTop, renderLoading);
  }
  return (
    <StyledSectionWrapperDiv $paddingTop={paddingTop}>
      <StyledSectionContentWrapperDiv $isDisabled={isDisabled}>
        <StyledSectionHeaderWrapperDiv>
          <StyledTitleWrapperDiv>
            <StyledSectionTitleDiv>{title}</StyledSectionTitleDiv>
            {button}
          </StyledTitleWrapperDiv>
          {description && <StyledDescriptionWrapperDiv>{description}</StyledDescriptionWrapperDiv>}
        </StyledSectionHeaderWrapperDiv>

        <div>{children}</div>
      </StyledSectionContentWrapperDiv>
    </StyledSectionWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderLoadingState(paddingTop: number, renderLoading?: Renderer) {
  return (
    <StyledSectionWrapperDiv $paddingTop={paddingTop}>
      <StyledSectionHeaderWrapperDiv>
        <StyledTitleWrapperDiv>
          <StyledSectionTitleDiv>
            <Skeleton height="16px" width="214px" scheme={SkeletonColorSchemesEnum.GREY30} />
          </StyledSectionTitleDiv>
        </StyledTitleWrapperDiv>
      </StyledSectionHeaderWrapperDiv>
      {renderLoading && <div>{renderLoading()}</div>}
    </StyledSectionWrapperDiv>
  );
}
